
import React from "react";
import { FormControls, i18nManager } from "../../../i18n";
import { CSSTransition } from 'react-transition-group';
import { Schemes, Subscriber, Utils } from "../../../utils";
import { useSearchParams } from "react-router-dom";
import { CreateTransaction, Elements, Siren } from "../../shared";
import { ApiResponseData, BaseService, TransactionService, SanitizedResponse, ITransaction, PloutousStatus, PloutousUtils, CurrencyService, ICurrency, IProfile } from "../../../services";
import { BoolHelper, Button, Classname, Column, DataManageRef, Popover, Table, Transition } from "@rivtn/noseur";
import { AcceptRejectTransaction } from "./transaction/shared/AcceptRejectTransaction";
import { RefundTransaction } from "./transaction/shared/RefundTransaction";

export interface TransactionsProps {
    profile: IProfile;
}

function Transactions(props: TransactionsProps) {
    const popoverManageRef = React.useRef<any>(null);
    const transitionNodeRef = React.useRef<any>(null);
    const currencyService = CurrencyService.getInstance();
    const tableManageRef = React.useRef<DataManageRef>(null);
    const transactionService = TransactionService.getInstance();
    const [showFilters, setShowFilters] = React.useState<boolean>();
    const labels = i18nManager.Labels.dashboard.ploutous.transactions;
    const [selectedCurrency, setSelectedCurrency] = React.useState<ICurrency>();
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const [activeTransaction, setActiveTransaction] = React.useState<ITransaction>();
    const [currencies, setCurrencies] = React.useState<ApiResponseData<ICurrency>>({});
    const [transactions, setTransactions] = React.useState<ApiResponseData<ITransaction>>({});

    React.useEffect(fetchCurrencies, []);
    React.useEffect(fetchTransactions, [searchParams]);
    React.useEffect(() => {
        if (!searchParams.has("trans_status")) return;
        Siren.alert((labels.redirects_messages as any)[searchParams.get("trans_status") as string]);
    }, []);

    return (<div className="charon-messages">
        <div className="management transactions">
            <div className="trans-cards">
                {labels.stats.map((stat: { id: string; color: string; desc: string; label: string; }) => (<div key={stat.id} className="trans-card" style={{
                    borderColor: stat.color,
                    boxShadow: `0px 4px 4px ${stat.color}40`,
                }}>
                    <div className="header">
                        {Elements.buildTransCardLogo(stat.color)}
                        <span>{stat.label}</span>
                    </div>
                    <div className="content">
                        <span className="amount">{selectedCurrency?.sign} 0.00</span>
                        <span className="desc">{stat.desc}</span>
                    </div>
                </div>))}
            </div>
            <div className="controls" style={{ marginTop: 0 }}>
                <div>
                    <Button scheme={Schemes.RIVTN_QUIRINUS} text={labels.filter} leftIcon="fa fa-filter" raised outlined={!showFilters}
                        onClick={() => setShowFilters(!showFilters)} style={{ marginLeft: 0 }} />
                    <span className="link" onClick={exportTransactionAsCsvToFileSystem}>{labels.export_csv}</span>
                </div>
                <div>
                    <Button scheme={Schemes.RIVTN_QUIRINUS} text={labels.initiate_transaction} raised
                        onClick={() => {
                            CreateTransaction({
                                profile: props.profile,
                                onComplete: window.location.replace
                            }).show();
                        }} />
                    {Elements.buildConfirmDialogForm([
                        {
                            ...labels.currency_control,
                            options: currencies?.content?.map((currency) => ({ "value": currency.external_id, ...currency })),
                            extras: {
                                clearable: false,
                                optionMap: {
                                    value: "external_id",
                                    label: "{name} - {code}"
                                },
                                onSelectOptions: (currency: ICurrency) => {
                                    setSelectedCurrency(currency);
                                    fetchTransactions();
                                }
                            }
                        },
                        labels.date_range_control], (name, value) => {
                            if (name === "currency_id") {
                                currencyService.queryCurrencies({ name: value.value }).then(({ apiResponse }: SanitizedResponse<ICurrency>) => {
                                    value.manageRef?.changeOptions(apiResponse.data!.content?.map((currency) => ({ "value": currency.external_id, ...currency })));
                                }).catch(BaseService.reportError);
                            }
                        }, searchParams, undefined, { margin: 0, display: "flex" }, undefined, { marginTop: 0, marginLeft: 20, minWidth: "14rem" }, setSearchParams, undefined, undefined, { platform_id: "messages_platform_id" })}
                </div>
            </div>
            <CSSTransition classNames={Transition.CURTAIN} timeout={500} in={showFilters} nodeRef={transitionNodeRef} unmountOnExit>
                <div className="filters" ref={transitionNodeRef}>
                    {labels.filters.form?.map((control: FormControls, index: number) => {
                        return (<div key={index} className="filter">
                            <span>{control.title}</span>
                            {Elements.buildConfirmDialogForm([control], (name, value) => {
                                if (name === "business_id") {
                                    console.log("FOR BUSINESS ID", name, value)
                                    /*channelService.queryChannel({ name: value.value }, true).then(({ apiResponse }: SanitizedResponse<ITransaction>) => {
                                        value.manageRef?.changeOptions(apiResponse.data!.content?.map((channel: ITransaction) => {
                                            return {
                                                "label": channel.name,
                                                "value": channel.channel_id
                                            };
                                        }));
                                    }).catch(BaseService.reportError);*/
                                }
                            }, searchParams, undefined, { marginTop: 0 }, undefined, { marginTop: 7 }, setSearchParams, undefined, undefined, { platform_id: "messages_platform_id" })}
                        </div>);
                    })}
                    <Button scheme={Schemes.RIVTN_QUIRINUS} text={labels.filters.reset_filter} outlined onClick={() => {
                        labels.filters.form.map((control: FormControls) => {
                            Utils.updateSearchParams((control.name === "platform_id" ? "messages_platform_id" : control.name), undefined, setSearchParams);
                        });
                    }} />
                    <Button scheme={Schemes.RIVTN_QUIRINUS} text={labels.filters.apply} style={{ padding: "10px 40px" }} onClick={() => fetchTransactions()} />
                </div>
            </CSSTransition>
            <Table className="table" data={transactions.content} dataRefreshKeys={["id", "status"]} rowsPerPage={transactions.size ?? 100} totalRecords={transactions.total_elements} loadingState={Elements.LOADING_STATE} manageRef={tableManageRef} emptyState={<div className="es" style={{ marginTop: 20 }}>
                <i className="fa fa-bullseye" />
                <span style={{ maxWidth: 150 }}>{i18nManager.Labels.common.no} {labels.fine_name} {i18nManager.Labels.common.found.toLowerCase()} <span onClick={() => {
                    CreateTransaction({
                        profile: props.profile,
                        onComplete: window.location.replace
                    }).show();
                }}>{labels.initiate}</span> {i18nManager.Labels.common.a_new} {labels.fine_name}</span>
            </div>}>
                <Column sortable dataKey="transaction_id" header={labels.transaction.table.transaction_id} />
                <Column sortable header={labels.transaction.table.business_service} template={(transaction: ITransaction) => (`${transaction.extra_data?.business_name ?? ''} (${transaction.extra_data?.service_name ?? ''})`)} />
                <Column sortable header={labels.transaction.table.payment_methods} template={(transaction: ITransaction) => transaction.extra_data?.request_payment_methods?.map(m => Utils.toSaneSentenceFormat(m))?.join(", ")} />
                <Column sortable header={labels.transaction.table.processors} template={(transaction: ITransaction) => Object.keys(transaction.extra_data?.payment_processors ?? {}).map(k => transaction.extra_data?.payment_processors[k].name).join(", ")} />
                <Column sortable header={labels.transaction.table.amount} template={(transaction: ITransaction) => {
                    if (transaction.amount !== 0) {
                        return `${selectedCurrency?.sign} ${Utils.makeToMoney(transaction.charge_amount)} (${Utils.makeToMoney(transaction.amount)})`;
                    }
                    return `${selectedCurrency?.sign} ${Utils.makeToMoney(transaction.charge_amount)}`;
                }} />
                <Column sortable dataKey="created_at" header={labels.transaction.table.date} template={Utils.formatToFineDate5} />
                <Column sortable dataKey="status" header={labels.transaction.table.status} template={(status) => PloutousUtils.selectPloutousStatusElement(status)} />
                <Column template={(transaction: ITransaction) => (<i className={Classname.build("fa fa-ellipsis-h")} style={{ cursor: "pointer", margin: "5px 30px 10px 10px" }}
                    onClick={(e: any) => {
                        setActiveTransaction(transaction);
                        popoverManageRef.current?.toggle(e);
                    }} />)} />
            </Table> 
            {Elements.buildPaginator(transactions ?? { size: 100 }, ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, id: null, size }, setSearchParams), parseInt(searchParams.get("page") ?? "1"))}

            <Popover className="dashboard-popover" manageRef={popoverManageRef} style={{ padding: "10px 0px" }} dismissOnClick>
                <span className="popover-link" onClick={() => window.location.href = `/dashboard/finance/transactions/${activeTransaction?.external_id}`}>{labels.transaction.popover.view_transaction}</span>
                {BoolHelper.equalsAny(activeTransaction?.status, [PloutousStatus.TRANSACTION_FULLY_PAID_PENDING_ACKNOWLEDGEMENT, PloutousStatus.TRANSACTION_PARTIALLY_PAID])
                    && (<>
                        <span className="popover-link g" onClick={() => {
                            AcceptRejectTransaction({
                                type: "accept_transaction",
                                onComplete: fetchTransactions,
                                transaction: activeTransaction!,
                            }).show();
                        }}>{labels.transaction.popover.accept}</span>
                        <span className="popover-link r" onClick={() => {
                            AcceptRejectTransaction({
                                type: "reject_transaction",
                                onComplete: fetchTransactions,
                                transaction: activeTransaction!,
                            }).show();
                        }}>{labels.transaction.popover.reject}</span>
                    </>)}
                {BoolHelper.equalsAny(activeTransaction?.status, [PloutousStatus.TRANSACTION_FULLY_PAID_FULLY_ACKNOWLEDGED_ACCEPTED, PloutousStatus.TRANSACTION_FULLY_PAID_PARTIALLY_ACKNOWLEDGED_ACCEPTED,
                PloutousStatus.TRANSACTION_FULLY_PAID_PARTIALLY_ACKNOWLEDGED_ACCEPTED_AND_PARTIALLY_REFUNDED, PloutousStatus.TRANSACTION_EXPIRES_WITH_PARTIAL_PAYMENT, PloutousStatus.TRANSACTION_PARTIALLY_REFUNDED,
                PloutousStatus.TRANSACTION_PARTIAL_REFUND_INITIATED, PloutousStatus.TRANSACTION_PARTIALLY_PAID_PARTIALLY_ACKNOWLEDGED_ACCEPTED_AND_PARTIAL_REFUND_INITIATED])
                    && (<span className="popover-link y" onClick={() => {
                        RefundTransaction({
                            onComplete: fetchTransactions,
                            transaction: activeTransaction!,
                        }).show();
                    }}>{labels.transaction.popover.refund}</span>)}
                <span className="popover-link" onClick={() => {
                    window.location.href = `/dashboard/finance/transactions/${activeTransaction?.external_id}?view=NOTIFICATIONS`;
                }}>{labels.transaction.popover.manage_notifications}</span>
                <span className="popover-link" onClick={() => {
                    window.location.href = `/dashboard/finance/transactions/${activeTransaction?.external_id}?view=TRAILS`;
                }}>{labels.transaction.popover.view_trails}</span>
            </Popover>
        </div>
    </div>);

    function fetchCurrencies() {
        const selectedCurrencyId = searchParams.get("currency_id");
        currencyService.queryCurrencies({ status: "ACTIVE" }).then(({ apiResponse }: SanitizedResponse<ICurrency>) => {
            setCurrencies(apiResponse.data!);
            let currency = apiResponse.data?.content?.[0];
            if (selectedCurrencyId) {
                let curr = apiResponse.data?.content?.find(c => c.external_id == selectedCurrencyId);
                if (curr) currency = curr;
            }
            if (currency) setSelectedCurrency(currency);
            fetchTransactions(currency);
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function fetchTransactions(currency?: ICurrency) {
        if (!currency && !selectedCurrency) return;
        tableManageRef.current?.setLoadingState(true);
        transactionService.queryTransactions(Utils.normalizeUrlParams(searchParams, undefined, { size: searchParams.get("size") ?? 100 })).then(({ apiResponse }: SanitizedResponse<ITransaction>) => {
            setTransactions(apiResponse.data!);
        }).catch(BaseService.reportError).finally(() => {
            tableManageRef.current?.setLoadingState(false);
            Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED);
        });
    }

    function exportTransactionAsCsvToFileSystem() {
        const csvHeader = `"Date","Transaction Id","Reference","Request Currency","Request Amount","Currency","Amount","Business (Service)","Payment Methods","Processors","Account Number","Msisdn","Status"\n`;
        const csv = transactions?.content?.map(transaction => {
            return `"${transaction.created_at}","${transaction.external_id}","${transaction.reference}","${transaction.request_currency_code}","${Utils.makeToMoney(transaction.request_amount)}",` +
                `"${transaction.charge_currency_code}","${Utils.makeToMoney(transaction.charge_amount)}","${transaction.extra_data.business_name} (${transaction.extra_data.service_name})",` +
                `"${transaction.extra_data?.request_payment_methods?.map(m => Utils.toSaneSentenceFormat(m))?.join(", ")}","${Object.keys(transaction.extra_data?.payment_processors ?? {}).map(k => transaction.extra_data?.payment_processors[k].name).join(", ")}",` +
                `"${transaction.payer_account_number}","${transaction.msisdn}","${PloutousUtils.selectPloutousStatusLabel(transaction.status)}"`;
        }).join('\r\n') ?? "";
        Utils.downloadBlob(csvHeader + csv, `Ploutous Transactions [date range] ${selectedCurrency?.code}.csv`);
    }

}

export default Transactions;
